import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import React from 'react';

import {assert} from 'Asserts';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {GridPreviewQuery, GridPreviewQueryVariables} from 'samples/grids/__generated/GridPreviewQuery';
import GridPreview from 'samples/grids/GridPreview';
import {isBoxSchemaGraphQL, isPlateSchemaGraphQL} from 'schemas/SchemaPredicates';
import {GRAY_DARKER, GRAY_LIGHTER} from 'ui-library/Colors';

import {StorableChipSummaryGraphQL} from '../../../graphql-fragments/inventory/__generated/StorableChipSummaryGraphQL';
import {StorableSchemaGraphQL} from '../../../graphql-fragments/inventory/schemas/__generated/StorableSchemaGraphQL';

type Storable = GridPreviewQuery['storable'];

type GridCellsInfo = ExtractByTypename<Storable, 'BoxNode'>;

type StorableChipBoxInfo = ExtractByTypename<StorableChipSummaryGraphQL, 'BoxNode'>;

type StorableChipPlateInfo = ExtractByTypename<StorableChipSummaryGraphQL, 'PlateNode'>;

type BoxSchemaGraphQL = StorableChipBoxInfo['storableSchema'];

type PlateSchemaGraphQL = StorableChipPlateInfo['storableSchema'];

export const GRID_PREVIEW_QUERY: DocumentNode = gql`
  query GridPreviewQuery($id: StrictString!) @skipAudit @engComponent(name: INVENTORY) {
    storable(id: $id) {
      ... on StorableInterface {
        id
      }
      ... on GridInterface {
        cells {
          rowIndex
          columnIndex
          container {
            ... on ContainerNode {
              id
              checkOutStatusInfo: checkOutStatusInfoJson
              contents {
                containedItem {
                  ... on RegistryItemInterface {
                    id
                    name
                  }
                  ...InaccessibleInfoGraphQL
                }
                concentrationJson
              }
              quantityJson
              restrictionStatus
              sampleRoles {
                id
                isCurrentUserRestrictedSampleUser
                sampleOwners {
                  ... on PartyInterface {
                    id
                    name
                    handle
                  }
                }
              }
              expirationInfo {
                id
                isExpired
                isTrackingEnabled
              }
            }
            ...InaccessibleInfoGraphQL
          }
        }
      }
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface Props {
  grid: StorableChipBoxInfo | StorableChipPlateInfo;
  widthPx: number; // figures out height based on width so that cells are squares
}

export default function QueryingGridPreview(props: Props): JSX.Element {
  function isGridCellsInfo(storable: Storable): storable is GridCellsInfo {
    return ['BoxNode', 'PlateNode'].includes(storable.__typename);
  }

  function gridCellsInfo(storable: Storable): GridCellsInfo {
    assert(isGridCellsInfo(storable), `Expected ${storable} to be a grid`);
    return storable;
  }

  function gridSchema(storableSchema: StorableSchemaGraphQL): BoxSchemaGraphQL | PlateSchemaGraphQL {
    assert(
      isPlateSchemaGraphQL(storableSchema) || isBoxSchemaGraphQL(storableSchema),
      `Expected ${storableSchema} to be a box or plate schema`
    );
    return storableSchema;
  }

  const heightPx =
    props.widthPx *
    (gridSchema(props.grid.storableSchema).height / gridSchema(props.grid.storableSchema).width);

  return (
    <BenchlingQuery<GridPreviewQuery, GridPreviewQueryVariables>
      query={GRID_PREVIEW_QUERY}
      fetchPolicy="cache-and-network"
      variables={{id: props.grid.id}}
      renderLoading={() => (
        <svg width={props.widthPx} height={heightPx}>
          <rect x="0" y="0" width={props.widthPx} height={heightPx} fill={GRAY_LIGHTER} />
          <text x="3" y="15" fill={GRAY_DARKER}>
            Loading preview...
          </text>
        </svg>
      )}
    >
      {(data) => (
        <GridPreview
          grid={{...gridCellsInfo(data.storable), storableSchema: gridSchema(props.grid.storableSchema)}}
          widthPx={props.widthPx}
          heightPx={heightPx}
        />
      )}
    </BenchlingQuery>
  );
}
